import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to detect user inactivity and trigger timeout after specified duration
 * Only active on public (non-admin) pages
 * 
 * @param {number} timeoutMinutes - Minutes of inactivity before timeout
 * @param {function} onTimeout - Callback when timeout occurs
 * @param {boolean} enabled - Whether idle detection is enabled
 */
export const useIdleTimeout = (timeoutMinutes = 15, onTimeout, enabled = true) => {
  const [isIdle, setIsIdle] = useState(false);
  const timeoutRef = useRef(null);
  const location = useLocation();

  // Check if current page should be tracked
  const shouldTrack = () => {
    const path = location.pathname;
    // Don't track admin pages or login
    if (path.startsWith('/admin') || 
        path.startsWith('/super-admin') || 
        path === '/login') {
      return false;
    }
    return enabled;
  };

  const resetTimer = () => {
    if (!shouldTrack()) return;

    setIsIdle(false);
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setIsIdle(true);
      if (onTimeout) {
        onTimeout();
      }
    }, timeoutMinutes * 60 * 1000);
  };

  useEffect(() => {
    if (!shouldTrack()) {
      // Clear any existing timeouts on admin pages
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any activity
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeoutMinutes, enabled, location.pathname]);

  return { isIdle };
};
